import type { StyleSpecification } from 'maplibre-gl';
import {
  BASE_IMAGERY,
  CHART_OVERLAYS,
  BaseImageryId,
  ChartOverlayId,
} from './sources';

/**
 * Build a minimal MapLibre style with a two-tier basemap stack.
 *
 *   imagery         — always present; either OSM streets or Esri imagery
 *   chart (opt.)    — VFR/IFR/helo aeronautical chart layered on top
 *
 * Outside the chart's published coverage the chart 404s its tiles and
 * the imagery shows through (e.g. ocean bathymetry under a Sectional).
 *
 * `chartOpacity` (default 0.9) is the user-controllable chart blend
 * factor. Range 0–1 — 0.85 lets satellite ocean show through more,
 * 0.99 keeps the chart almost-opaque. Anything < 1 forces the painter
 * to take the blended path so imagery underneath gets drawn.
 */
export function buildStyle(
  imageryId: BaseImageryId,
  chartId: ChartOverlayId,
  chartOpacity = 0.9,
): StyleSpecification {
  const imagery = BASE_IMAGERY[imageryId];
  const chart = chartId === 'none' ? null : CHART_OVERLAYS[chartId];

  // 'basic' is the same OSM tiles as 'street' but rendered dimmer so a
  // chart overlay on top still reads cleanly. ForeFlight does this with
  // a true dim-streets layer; we approximate by lowering raster opacity.
  const imageryOpacity = imageryId === 'basic' ? 0.55 : 1.0;

  const sources: StyleSpecification['sources'] = {
    imagery: {
      type: 'raster',
      tiles: imagery.tiles,
      tileSize: imagery.tileSize ?? 256,
      attribution: imagery.attribution,
      minzoom: imagery.minzoom ?? 0,
      maxzoom: imagery.maxzoom ?? 19,
    },
  };
  if (chart) {
    sources.chart = {
      type: 'raster',
      tiles: chart.tiles,
      tileSize: chart.tileSize ?? 256,
      attribution: chart.attribution,
      minzoom: chart.minzoom ?? 0,
      maxzoom: chart.maxzoom ?? 18,
    };
  }

  const layers: StyleSpecification['layers'] = [
    {
      id: 'imagery',
      type: 'raster',
      source: 'imagery',
      paint: { 'raster-opacity': imageryOpacity },
    },
  ];
  if (chart) {
    // raster-opacity must be strictly < 1 — at 1.0 (or even 0.99/0.95)
    // MapLibre 4.7.1's opaque-fast-path culls the imagery layer
    // underneath, including in regions where the chart 404s its tiles
    // (Pacific Ocean, outside FAA chart coverage). We empirically
    // landed on 0.9 as the threshold that reliably forces the painter
    // into the blended path on prod. Anything we let the user pick
    // here gets clamped to that ceiling.
    //
    // Long-term, switching to a chart proxy that returns truly
    // transparent PNG outside coverage would let us push to 1.0 and
    // remove this dance. Tracked in STATUS.md follow-ups.
    const safeOpacity = Math.max(0, Math.min(0.92, chartOpacity));
    // Layer-level maxzoom: past chart's native resolution + a small
    // overzoom margin, the chart pixels are 4×+ stretched and look
    // grey/blurry. Stopping the layer rendering past that point lets
    // the imagery layer show through cleanly so helo pilots can zoom
    // tight on a helipad without the chart wrecking the picture.
    // Sectional native = 12 → disappears at z=15 (imagery solo from there).
    // Helo native = 13       → disappears at z=16.
    // IFR Low native = 11    → disappears at z=14.
    // IFR High native = 10   → disappears at z=13.
    const chartNativeMax = chart.maxzoom ?? 12;
    const chartLayerMaxZoom = chartNativeMax + 3;
    // Auto-fade by zoom: at and below chart native max, full opacity
    // (whatever the slider says); from chart-native to layer-maxzoom
    // we linearly fade the chart out so the close-in layer (topo /
    // satellite / OSM) shines through naturally without the user
    // having to grab the slider after every zoom-in.
    //
    // The interpolate expression is a MapLibre data-driven property:
    // at zooms <= chartNativeMax it returns safeOpacity, between
    // chartNativeMax and chartLayerMaxZoom it linearly drops to 0,
    // and the layer-level maxzoom hides it entirely past that.
    //
    // We `as unknown as number` to bypass MapLibre's narrow paint
    // typedefs. The runtime accepts expression arrays here even though
    // the static type only declares the camera-zoom function form.
    const opacityExpr = [
      'interpolate', ['linear'], ['zoom'],
      0,                  safeOpacity,
      chartNativeMax,     safeOpacity,
      chartLayerMaxZoom,  0,
    ];
    layers.push({
      id: 'chart',
      type: 'raster',
      source: 'chart',
      maxzoom: chartLayerMaxZoom,
      paint: {
        'raster-opacity': opacityExpr as unknown as number,
        'raster-fade-duration': 0,  // no grey-flash during tile load; satellite shows through cleanly
      },
    });
  }

  return {
    version: 8,
    glyphs: 'https://demotiles.maplibre.org/font/{fontstack}/{range}.pbf',
    // ForeFlight-style globe projection at low zoom that auto-flattens
    // to mercator around z=12. Baked into the style spec so it survives
    // setStyle() calls without us having to re-call setProjection() on
    // every chart/imagery toggle (which triggered a MapLibre 5.24
    // worker-arg race condition: 'Cannot read properties of undefined
    // (reading signal)').
    projection: { type: 'globe' },
    sources,
    layers,
  };
}
